<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('external_coach_billing.title') }}
        </h6>
      </div>

      <div class="element-box">
        <Menu :countUnreadMessageForCoordinator="countUnreadMessageForCoordinator"/>

        <Search
            :filters="filters"
            :loading="isSearching"
            :coachDropdown="coachDropdown"
            @search="getPendingCoachBills"/>

        <PerPage
            class="mt-3"
            :values="[10, 25, 50, 100]"
            @update:perPage="getPendingCoachBills"/>

        <EmptyState
            v-if="!coachBills.data?.length"
            icon="fa-file-invoice"
            title="Keine Rechnungen gefunden"
            subtitle="Es wurden keine Rechnungen gefunden, die Ihren Suchkriterien entsprechen."/>

        <form v-else @submit.prevent="" method="post">
          <div class="table-responsive overflow-auto pt-4">
            <table class="table table-striped">
              <thead>
              <tr>
<!--                <th style="width: 3%;"></th>-->
                <th>Rechnungs-Nr.</th>
                <th style="width: 20%;">Coach</th>
                <th>Kreditor</th>
                <th>Abrechnungsart</th>
                <th>Rechnungsdatum</th>
                <th class="text-end"># Sitzungen</th>
                <th class="text-end">Rechnungsbetrag</th>
                <th class="text-start"><span class="ps-5">Status</span></th>
                <th style="width: 5%;"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(coachBill) in coachBills.data" :key="coachBill.id">
<!--                <td class="text-center">-->
<!--                  <input type="checkbox" v-model="selectedBillIds" :value="coachBill.id"/>-->
<!--                </td>-->
                <td>GS {{ coachBill?.billNumber }}</td>
                <td>{{ coachBill?.coach?.firstName }} {{ coachBill?.coach?.lastName }}</td>
                <td>{{ coachBill?.coach?.creditor}}</td>
                <td>
                  <span v-if="coachBill.coachingType === 'group_coaching'">
                      Gruppenmaßnahmen - {{ coachBill.coachBillingTypeTranslation }}
                  </span>
                  <span v-if="coachBill.coachingType === 'case_management_coaching'">
                      DRV Fallmanagement
                  </span>
                  <span v-else>
                      Einzelcoaching
                  </span>
                </td>
                <td>{{ coachBill.createdAt }}</td>
                <td class="text-end">{{ coachBill.totalUnits }}</td>
                <td class="text-end">{{ coachBill.sum }}
                </td>
                <td class="text-start" style="min-width: 160px">
                  <div class="ps-3">
                    <div v-if="coachBill.status === 'billed' || coachBill.status === 'approved'"
                         class="status-pill green p-2 me-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-bs-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    <div v-else-if="coachBill.status === 'pending'"
                         class="status-pill yellow p-2 me-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-bs-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    <div v-else
                         class="status-pill red p-2 me-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-bs-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    {{ coachBill.statusTranslation }}
                  </div>
                </td>
                <td class="text-end">
                  <Buttons :coachBill="coachBill"
                           @rejectBill="rejectBillModal"
                           @approveBill="approveBillModal"/>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <Pagination
              v-if="coachBills?.pagination?.total_pages > 1"
              :totalPages="coachBills?.pagination?.total_pages"
              :currentPage="coachBills?.pagination?.current_page"
              :totalItems="coachBills?.pagination?.total_items"
              :perPage="coachBills?.pagination?.items_per_page"
              @pagechanged="getPendingCoachBills"/>
        </form>

      </div>
    </div>

    <Modal
        v-model="showRejectBillModal"
        title="Rechnung ablehnen"
        :hide-cancel="true"
        :button-label="$t('general.button.save')"
        sizeModal="modal-lg"
        :hide-footer="true"
        classFooter="justify-content-center">
      <RejectBillForm
          :coachBillId="coachBillId"
          v-model:submitForm="submitForm"/>
    </Modal>

    <ModalConfirm
        v-model="showApproveBillModal"
        :confirmationQuestion="t('general.approve_question')"
        header=""
        :onConfirm="confirmApproveBill"/>

  </PageTemplate>
</template>


<script setup>
import coachBillingService from '@/api/CoachBilling';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import { getExternalCoachForCoordinatorDropdown } from '@/shared/dropdowns';
import Menu from '@/views/App/CoachBilling/Coordinator/Menu.vue';
import Buttons from '@/views/App/CoachBilling/Coordinator/PendingBills/Buttons.vue';
import RejectBillForm from '@/views/App/CoachBilling/Coordinator/PendingBills/RejectBillForm.vue';
import Search from '@/views/App/CoachBilling/Coordinator/PendingBills/Search.vue';
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t }                            = useI18n();
const coachBills                       = ref([]);
const coachDropdown                    = ref([]);
const selectedBillIds                  = ref([]);
const isSearching                      = ref(false);
const isLoaded                         = ref(false);
const showApproveBillModal             = ref(false);
const showRejectBillModal              = ref(false);
const coachBillId                      = ref('');
const submitForm                       = ref(false);
const countUnreadMessageForCoordinator = ref(0);

const filters = ref({
  coachId: '',
});

watch(submitForm, () => {
  coachBillId.value          = '';
  showRejectBillModal.value  = false;
  showApproveBillModal.value = false;
  getPendingCoachBills();
});

async function getPendingCoachBills(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = coachBills.value?.pagination?.items_per_page;

  try {
    const { data } = await coachBillingService.coordinatorGetPendingBills({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    coachBills.value  = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function approveBillModal(id) {
  coachBillId.value          = '';
  showApproveBillModal.value = true;
  showRejectBillModal.value  = false;
  coachBillId.value          = id;
  CloseToast();
}

function rejectBillModal(id) {
  coachBillId.value          = '';
  showRejectBillModal.value  = true;
  showApproveBillModal.value = false;
  coachBillId.value          = id;
  CloseToast();
}

async function confirmApproveBill() {
  try {
    await coachBillingService.coordinatorApproveBill(coachBillId.value);

    coachBillId.value          = '';
    showApproveBillModal.value = false;
    showRejectBillModal.value  = false;

    SuccessToast(t('general.toasts.save_success'));

    await getPendingCoachBills();
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function countUnreadMessages() {
  try {
    const { data } = await coachBillingService.coordinatorCountUnreadMessages();

    countUnreadMessageForCoordinator.value = data.data.count;
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getPendingCoachBills(),
    getExternalCoachForCoordinatorDropdown(coachDropdown),
    countUnreadMessages(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>